import React, { useState, useEffect, useRef } from 'react';
import { uploadLogo, getLogoUrl, deleteLogo } from '../../../services/logoService';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const LogoUploadTab = () => {
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadLogo();
  }, []);

  // Auto-clear messages after 5 seconds
  useEffect(() => {
    if (message?.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const loadLogo = async () => {
    const url = await getLogoUrl();
    setLogoUrl(url);
    setPreviewUrl(url);
  };

  const handleFileSelect = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes?.includes(file?.type)) {
        setMessage({ 
          type: 'error', 
          text: 'Invalid file type. Please upload JPG, PNG, WebP, or SVG.' 
        });
        // Reset file input
        if (fileInputRef?.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Validate file size (2MB = 2097152 bytes)
      const maxSize = 2097152;
      if (file?.size > maxSize) {
        const fileSizeMB = (file?.size / 1048576)?.toFixed(2);
        setMessage({ 
          type: 'error', 
          text: `File size (${fileSizeMB}MB) exceeds 2MB limit. Please choose a smaller file.` 
        });
        // Reset file input
        if (fileInputRef?.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Show preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader?.result);
      };
      reader?.readAsDataURL(file);

      // Upload file
      handleUpload(file);
    }
  };

  const handleUpload = async (file) => {
    setUploading(true);
    setMessage({ type: '', text: '' });

    const result = await uploadLogo(file);

    if (result?.success) {
      setLogoUrl(result?.url);
      setPreviewUrl(result?.url);
      setMessage({ type: 'success', text: 'Logo uploaded successfully!' });
      // Reset file input
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
    } else {
      setMessage({ type: 'error', text: result?.error || 'Failed to upload logo' });
      setPreviewUrl(logoUrl); // Revert to previous logo
      // Reset file input
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
    }

    setUploading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the current logo?')) {
      return;
    }

    setDeleting(true);
    setMessage({ type: '', text: '' });

    const result = await deleteLogo();

    if (result?.success) {
      setLogoUrl(null);
      setPreviewUrl(null);
      setMessage({ type: 'success', text: 'Logo deleted successfully!' });
    } else {
      setMessage({ type: 'error', text: result?.error || 'Failed to delete logo' });
    }

    setDeleting(false);
  };

  const handleUploadClick = () => {
    fileInputRef?.current?.click();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Application Logo</h2>
        <p className="text-sm text-gray-600 mb-6">
          Upload a custom logo for your application. The logo will appear in the navigation bar.
          Recommended size: 200x200px. Maximum file size: 2MB.
        </p>

        {/* Message Display */}
        {message?.text && (
          <div
            className={`mb-4 p-4 rounded-lg ${
              message?.type === 'success' ?'bg-green-50 text-green-800 border border-green-200' :'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon
                name={message?.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
                size={20}
                color={message?.type === 'success' ? '#16a34a' : '#dc2626'}
              />
              <span className="text-sm font-medium">{message?.text}</span>
            </div>
          </div>
        )}

        {/* Logo Preview */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Current Logo</label>
          <div className="flex items-center justify-center w-full h-48 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg relative">
            {uploading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
                <div className="text-center">
                  <Icon name="Loader2" size={48} color="#3b82f6" className="animate-spin mx-auto" />
                  <p className="mt-2 text-sm font-medium text-blue-600">Uploading...</p>
                </div>
              </div>
            )}
            {previewUrl && !uploading ? (
              <img
                src={previewUrl}
                alt="Application logo"
                className="max-h-40 max-w-full object-contain"
              />
            ) : !uploading ? (
              <div className="text-center">
                <Icon name="Image" size={48} color="#9ca3af" />
                <p className="mt-2 text-sm text-gray-500">No logo uploaded</p>
              </div>
            ) : null}
          </div>
        </div>

        {/* Upload Controls */}
        <div className="flex flex-wrap gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/svg+xml"
            onChange={handleFileSelect}
            disabled={uploading || deleting}
            className="hidden"
          />
          <Button
            variant="primary"
            onClick={handleUploadClick}
            disabled={uploading || deleting}
          >
            {uploading ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Icon name="Upload" size={16} />
                {logoUrl ? 'Replace Logo' : 'Upload Logo'}
              </>
            )}
          </Button>

          {logoUrl && (
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={uploading || deleting}
            >
              {deleting ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Icon name="Trash2" size={16} />
                  Delete Logo
                </>
              )}
            </Button>
          )}
        </div>

        {/* File Requirements */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-medium text-blue-900 mb-2">File Requirements:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Accepted formats: JPG, PNG, WebP, SVG</li>
            <li>• Maximum file size: 2MB</li>
            <li>• Recommended dimensions: 200x200px</li>
            <li>• Transparent backgrounds work best</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LogoUploadTab;